import React, { useEffect, useRef, useState } from 'react'
import { StyledBox } from './styles'
import SurveyTitle from './SurveyTitle'
import { useAppDispatch } from '../../store'
import { addSection, setEditableSurvey, resetState, setTitle, setDraftSurvey, createInitialSectionWithSetId } from '../../store/slices/SurveyTemplateSlice'
import { ButtonType } from '../../components/GenericButton/styles'
import AddIcon from '@mui/icons-material/Add'
import GenericButton from '../../components/GenericButton'
import useAsyncEffect from 'use-async-effect'
import { useParams } from 'react-router-dom'
import { getSurveyByIdApi } from '../../store/slices/SurveyTemplateSlice/apis'
import { getEditableSurvey, getLoading, getSurveySections, getSurveyTitle } from '../../store/slices/SurveyTemplateSlice/selectors'
import SurveySections from './SurveySections'
import SurveyHeader from './SurveyHeader'
import PageHeader from '../../components/PageHeader'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import PAGE_TITLES from '../../consts/titles'
import UnsavedBlockerModal from '../../components/UnsavedBlockerModal'
import { notification } from '../../components/Notifications'
import { isEqual } from 'lodash'

const SurveyTemplateFeature: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const loading = getLoading()
  const { id } = useParams()
  const title = getSurveyTitle()
  const sections = getSurveySections()
  const editableSurvey = getEditableSurvey()
  const storeKey = `templateDraft-${id ?? 'new'}`
  const [isDirty, setIsDirty] = useState(true)
  const retrievedFromApiAndStorage = useRef(false)

  const setSurveyByApi = async (): Promise<void> => {
    if (id) {
      const survey = await dispatch(getSurveyByIdApi({ urlId: id })).unwrap()
      dispatch(setEditableSurvey(survey))
    }
  }

  // compare original survey with current state for dirtiness
  useEffect(() => {
    const isTitleDirty = title !== (editableSurvey?.title ?? '')
    const isSectionsDirty = !isEqual(
      sections,
      editableSurvey?.surveySections ?? [createInitialSectionWithSetId(sections?.[0]?.surveySectionId)]
    )
    setIsDirty(isTitleDirty || isSectionsDirty)
  }, [title, sections, editableSurvey])

  // locally store new title and sections when updated
  useEffect(() => {
    if (!retrievedFromApiAndStorage.current) return
    if (!isDirty) {
      localStorage.removeItem(storeKey)
      return
    }
    localStorage.setItem(storeKey, JSON.stringify({ title, sections }))
  }, [title, sections, isDirty])

  const setSurveyByLocalStorage = (draft: string | null): void => {
    if (!draft) return
    setIsDirty(true)
    const parsed = JSON.parse(draft)
    dispatch(setTitle(parsed.title ?? title))
    dispatch(setDraftSurvey({ title: parsed.title ?? title, surveySections: parsed.sections ?? sections }))
    notification('Previous session ended without saving. Draft has been reloaded.', { variant: 'info' })
  }

  useAsyncEffect(async () => {
    // prevent if already retrieved from API or local storage
    if (retrievedFromApiAndStorage.current) return
    const draft = localStorage.getItem(storeKey)
    retrievedFromApiAndStorage.current = true
    dispatch(resetState())

    // fetch survey from API and set editable survey
    await setSurveyByApi()
    // load from local storage if it exists
    setSurveyByLocalStorage(draft)
  }, [id])

  const handleSection = (): void => {
    dispatch(addSection())
  }

  if (loading) {
    return <LoadingPageV2 title={PAGE_TITLES.SURVEY_TEMPLATE} />
  }

  return (
    <>
      <PageHeader
        title={title || PAGE_TITLES.UNTITLED_SURVEY_TEMPLATE}
        controls={<SurveyHeader setIsDirty={setIsDirty} isDirty = {isDirty} />}
        showBackButton isCompactOnly
      />
      <StyledBox>
        <SurveyTitle />
        <SurveySections />
        <GenericButton
          buttonType={ButtonType.Secondary}
          startIcon={<AddIcon />}
          label='Add Section'
          fontWeight='bold'
          onClick={handleSection}
        />
        <UnsavedBlockerModal isDirty={isDirty} storeKey={storeKey} />
      </StyledBox>
    </>
  )
}

export default SurveyTemplateFeature
